import { writeFileSync, mkdirSync } from 'node:fs';
import { readJsonFile } from './util';
import { homedir } from 'node:os';
import { join } from 'node:path';
import type { PermissionRule, Decision } from '../src/index';

export type PermConfig = { allow?: string[]; ask?: string[]; deny?: string[] };

/**
 * Parse CC-style permission rule strings from config into `PermissionRule`s.
 *
 * Grammar: `Tool` (whole tool) or `Tool(pathGlob)` (file-path-bearing tools). Precedence is encoded
 * by ORDER — `deny` first, then `allow`, then `ask` — because `PermissionPolicy.decide` is first-match.
 * So a `deny` always beats an `allow`/`ask` for the same call.
 *
 * The glob matches the call's `path` arg (file tools) OR its `command` arg (`bash`/`Shell`), so
 * `Edit(src/**)` gates edits under src and `Shell(git *)` / `bash(rm *)` gate by command pattern.
 */
const RULE_RE = /^(\w[\w*-]*)(?:\((.+)\))?$/; // tool names may carry `-` and a `*` wildcard (`mcp__browser-bridge__*`), but start with a word char (`*`, `**`, `-` stay rejected, as before)

function parseOne(raw: string, decision: Decision): PermissionRule | null {
  const m = RULE_RE.exec(raw.trim());
  if (!m) return null;
  const [, tool, pathGlob] = m;
  return pathGlob ? { tool, pathGlob, decision } : { tool, decision };
}

/** Build ordered rules (deny → allow → ask) from a config `permissions` block. Bad entries are skipped. */
export function parsePermRules(perms?: { allow?: string[]; ask?: string[]; deny?: string[] }, explicit = false): PermissionRule[] {
  if (!perms) return [];
  const out: PermissionRule[] = [];
  for (const [decision, list] of [['deny', perms.deny], ['allow', perms.allow], ['ask', perms.ask]] as const) {
    for (const raw of list ?? []) { const r = parseOne(raw, decision); if (r) out.push(explicit ? { ...r, explicit } : r); }
  }
  return out;
}

/** One-line human summary of a rule (for `/permissions`). */
export function describeRule(r: PermissionRule): string {
  return `${r.decision.padEnd(5)} ${r.tool ?? '*'}${r.pathGlob ? `(${r.pathGlob})` : ''}`;
}

// --- persisted rules (.agent/permissions.json) — written by "Always allow/deny" prompts ---
const PERM_FILE = (cwd: string) => join(cwd, '.agent', 'permissions.json');

/** Read persisted project rules (returns {} if absent/unreadable — never throws). */
export function loadPersistedRules(cwd: string): PermConfig {
  const j = readJsonFile<any>(PERM_FILE(cwd), null);
  return j ? { allow: j.allow ?? [], ask: j.ask ?? [], deny: j.deny ?? [] } : {};
}

/**
 * Read CC-compatible `.claude/settings.json` permission rules and merge them (user → project → project-local).
 * Same `Tool(arg)` string format as our own `permissions` block, so a Claude Code user's existing rules apply
 * as-is (e.g. `Read(./.env)`, `Edit(src/**)`). Malformed/absent files are ignored — never crashes a run.
 * (Tool-name caveat: CC's shell tool is `Bash`; ours are `bash`/`Shell` — shell rules may need renaming.)
 */
export function loadClaudeSettings(cwd: string, home: string = homedir()): PermConfig {
  const files = [join(home, '.claude', 'settings.json'), join(cwd, '.claude', 'settings.json'), join(cwd, '.claude', 'settings.local.json')];
  let out: PermConfig = {};
  for (const p of files) {
    // malformed/absent files fall back to null (logged) — config never crashes a run
    const perms = readJsonFile<any>(p, null)?.permissions;
    if (perms) out = mergePerms(out, { allow: perms.allow, ask: perms.ask, deny: perms.deny }) ?? out;
  }
  return out;
}

/** Append a rule string under a decision bucket and persist (idempotent — no duplicates). */
export function persistRule(cwd: string, decision: Decision, ruleStr: string): void {
  const cur = loadPersistedRules(cwd);
  const list = (cur[decision] ??= []);
  if (!list.includes(ruleStr)) list.push(ruleStr);
  try { mkdirSync(join(cwd, '.agent'), { recursive: true }); writeFileSync(PERM_FILE(cwd), JSON.stringify(cur, null, 2) + '\n'); }
  catch { /* best-effort; a remembered rule still applies for this session via the policy */ }
}

/** Merge two permission configs (later wins on duplicates; arrays concatenated + deduped). */
export function mergePerms(a?: PermConfig, b?: PermConfig): PermConfig | undefined {
  if (!a && !b) return undefined;
  const out: PermConfig = {};
  for (const k of ['allow', 'ask', 'deny'] as const) {
    const merged = [...(a?.[k] ?? []), ...(b?.[k] ?? [])];
    if (merged.length) out[k] = [...new Set(merged)];
  }
  return Object.keys(out).length ? out : undefined;
}

// --- directory trust ledger (~/.agent/trusted.json; path injectable for tests) ---
const TRUST_FILE = join(homedir(), '.agent', 'trusted.json');

/** Has the user previously trusted this working directory? */
export function isTrusted(cwd: string, file = TRUST_FILE): boolean {
  const list = readJsonFile<string[]>(file, []);
  return Array.isArray(list) && list.includes(cwd);
}

/** Record `cwd` as trusted (so future runs don't re-prompt). */
export function trustDir(cwd: string, file = TRUST_FILE): void {
  let list = readJsonFile<string[]>(file, []); // corrupt file resets (logged in readJsonFile)
  if (!Array.isArray(list)) list = [];
  if (!list.includes(cwd)) list.push(cwd);
  try { mkdirSync(join(file, '..'), { recursive: true }); writeFileSync(file, JSON.stringify(list, null, 2) + '\n'); }
  catch { /* best-effort */ }
}
